import { ZodCreateInvoiceSchema } from "@billingnow/zod-schema";
import { apiClient } from "./client";

export interface WebsiteProfile {
  name: string | null;
  legalName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  zipcode: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  taxId: string | null;
  knowledgeBase: string;
  website: string;
  /** Site logo, re-hosted on our blob store (null when none was found) */
  logo: string | null;
  /** Two clickable first-invoice examples matching the company's services */
  invoiceSuggestions: string[];
}

/**
 * Scrape + AI-analyze a company website (onboarding prefill / knowledge base).
 * Works before the organization exists — only auth is required.
 * `language` drives the language of the generated invoice suggestions.
 */
export const analyzeWebsite = async (url: string, language?: string): Promise<WebsiteProfile> => {
  const response = await apiClient.post<{ profile: WebsiteProfile }>(
    "/api/analyze-website",
    { url, language },
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data.profile;
};

export const getExchangeRates = async (
  currency: string,
): Promise<{ currency: string; rates: Record<string, number> }> => {
  const response = await apiClient.get<{
    success: boolean;
    timestamp: number;
    base: string;
    date: string;
    rates: Record<string, number>;
  }>(`/api/exchange-rates`);

  if (!response.data) {
    return { currency, rates: { [currency]: 1 } };
  }

  const { rates } = response.data;

  if (!rates[currency]) {
    return { currency, rates: { ...rates, [currency]: 1 } };
  }

  const baseRate = rates[currency];
  const adjustedRates: Record<string, number> = {};

  Object.keys(rates).forEach((key) => {
    adjustedRates[key] = rates[key]! / baseRate!;
  });

  return { rates: adjustedRates, currency };
};

export const addImage = async (file: File): Promise<{ url: string; downloadUrl: string }> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post("/api/upload/image", formData);
  return response.data;
};

export const deleteImage = async (url: string): Promise<void> => {
  await apiClient.delete("/api/upload/image", { data: { url } });
};

export const getSwissQRBill = async (data: ZodCreateInvoiceSchema, headers: Record<string, string>) => {
  const response = await apiClient.post("/api/mobile/swiss-qr-bill", data, {
    headers: {
      ...headers,
    },
    responseType: "text",
  });

  return response.data as string; // SVG string
};
